// src/pages/ProductView.jsx
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { products as productsApi, shop as shopApi, cart as cartApi } from '../api';
import { useToast } from '../hooks/useToast';
import Icon from '../components/Icon';
import { formatMoney, getCurrencySymbol } from '../data/currencies';
import { isComposite } from '../utils/composite';
import PhotoGallery from '../components/PhotoGallery';
import ShareProductDialog from '../components/ShareProductDialog';

function unitLabel(u) {
  if (u === 'kg' || u === 'кг') return 'кг';
  if (u === 'l' || u === 'л')   return 'л';
  return 'шт';
}

export default function ProductView() {
  const { t } = useTranslation();
  const { shopId, productId } = useParams();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();

  const [product, setProduct] = useState(null);
  const [shop, setShop]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({});
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  const load = async () => {
    setLoading(true);
    try {
      const [shopData, list] = await Promise.all([
        shopApi.getById(shopId),
        productsApi.catalog(shopId).catch(() => []),
      ]);
      setShop(shopData);
      const p = list.find(x => x.id === productId);
      setProduct(p);
      if (p && Array.isArray(p.variants) && p.variants.length === 1) {
        const v = p.variants[0];
        if (v.attributeValues) setSelected({ ...v.attributeValues });
      }
    } catch (e) {}
    setLoading(false);
  };

  const variants = useMemo(
    () => (Array.isArray(product?.variants) ? product.variants : []),
    [product]
  );
  const hasVariants = variants.length > 1;
  const attributes = useMemo(
    () => (Array.isArray(product?.attributes) ? product.attributes : []),
    [product]
  );

  const attributeOptions = useMemo(() => {
    const out = {};
    for (const attrName of attributes) {
      const set = new Set();
      for (const v of variants) {
        const val = v.attributeValues?.[attrName];
        if (val) set.add(val);
      }
      out[attrName] = Array.from(set);
    }
    return out;
  }, [attributes, variants]);

  const matchedVariant = useMemo(() => {
    if (!hasVariants) return variants[0] || null;
    return variants.find(v =>
      attributes.every(attr => v.attributeValues?.[attr] === selected[attr])
    ) || null;
  }, [variants, attributes, selected, hasVariants]);

  const priceDisplay = useMemo(() => {
    if (!hasVariants) {
      return { kind: 'single', value: product?.salePrice || 0 };
    }
    if (matchedVariant) {
      return { kind: 'single', value: matchedVariant.salePrice || 0 };
    }
    const prices = variants.map(v => Number(v.salePrice) || 0).filter(p => p > 0);
    if (prices.length === 0) return { kind: 'single', value: 0 };
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return { kind: 'single', value: min };
    return { kind: 'range', min, max };
  }, [hasVariants, matchedVariant, variants, product]);

  const photos = useMemo(() => {
    if (matchedVariant && Array.isArray(matchedVariant.photos) && matchedVariant.photos.length > 0) {
      return matchedVariant.photos;
    }
    if (!product) return [];
    return product.photos?.length ? product.photos : (product.photo ? [product.photo] : []);
  }, [matchedVariant, product]);

  const isValueAvailable = (attrName, value) => {
    for (const v of variants) {
      if (v.attributeValues?.[attrName] !== value) continue;
      if (Number(v.stock) > 0) return true;
    }
    return false;
  };

  const toggleAttr = (attrName, value) => {
    setSelected(prev => {
      if (prev[attrName] === value) {
        const next = { ...prev };
        delete next[attrName];
        return next;
      }
      return { ...prev, [attrName]: value };
    });
  };

  const allAttrsSelected = attributes.every(a => selected[a]);
  const canAddToCart = !hasVariants
    || (matchedVariant && Number(matchedVariant.stock) > 0 && allAttrsSelected);

  const addToCart = () => {
    if (!canAddToCart) return;

    if (hasVariants && matchedVariant) {
      const variantPhoto = (Array.isArray(matchedVariant.photos) && matchedVariant.photos[0])
        || product.photo
        || (product.photos && product.photos[0])
        || null;
      const attrLine = attributes.map(a => selected[a]).filter(Boolean).join(' / ');
      const compositeName = attrLine ? `${product.name} · ${attrLine}` : product.name;
      cartApi.add(shopId, {
        id: product.id,
        variantId: matchedVariant.id,
        name: compositeName,
        salePrice: matchedVariant.salePrice,
        unit: product.unit,
        photo: variantPhoto,
      }, qty);
      show(t('productView.addedToCart') + ' · ' + compositeName + ' × ' + qty);
    } else {
      cartApi.add(shopId, product, qty);
      show(t('productView.addedToCart') + ' · ' + product.name + ' × ' + qty);
    }
    setTimeout(() => navigate(-1), 800);
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;
  if (!product) return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}><Icon name="chevronLeft" size={18} /></button>
        <h1 className="page-title">{t('common.error')}</h1>
      </div>
      <div className="empty-state">
        <div className="icon"><Icon name="alert" size={28} stroke={1.5} /></div>
        <p>{t("products.noProductsFound")}</p>
      </div>
    </div>
  );

  const displayStock = matchedVariant
    ? Number(matchedVariant.stock) || 0
    : (Number(product.stock) || 0);

  return (
    <div className="page" style={{ paddingBottom: shop?.cartEnabled ? 120 : 24 }}>
      {ToastView}

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}><Icon name="chevronLeft" size={18} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="page-subtitle">{shop?.name?.toUpperCase()}</p>
          <h1 className="page-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product?.name || ''}
          </h1>
        </div>
        <button
          onClick={() => setShareOpen(true)}
          aria-label={t('shareProduct.share')}
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            padding: '8px 10px',
            cursor: 'pointer',
            color: 'var(--fg)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontFamily: 'inherit',
            marginLeft: 6,
          }}
        >
          <Icon name="share" size={16} stroke={2.2} />
        </button>
      </div>

      {shareOpen && (
        <ShareProductDialog
          product={product}
          shop={shop}
          currency={shop?.currency || 'KGS'}
          onClose={() => setShareOpen(false)}
          onError={() => show(t('shareProduct.shareError'), 'error')}
          onSuccess={(res) => {
            if (res?.method === 'download-fallback') {
              show(t('shareProduct.downloadedAndCopied'), 'success');
            }
          }}
        />
      )}

      <div style={{ padding: '0 20px', marginBottom: 16 }}>
        <PhotoGallery photos={photos} alt={product.name} />
      </div>

      <div style={{ padding: '0 20px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4, lineHeight: 1.25, letterSpacing: '-0.02em' }}>
          {product.name}
        </h2>
        {product.article && (
          <p style={{ fontSize: 12, color: 'var(--fg-faint)', fontFamily: 'var(--font-mono)' }}>
            {t("products.article")}: {product.article}
          </p>
        )}

        {product.description && (
          <p style={{
            marginTop: 12,
            fontSize: 14, color: 'var(--fg-muted)',
            lineHeight: 1.6, whiteSpace: 'pre-wrap',
          }}>
            {product.description}
          </p>
        )}

        {isComposite(product) && (
          <div style={{ marginTop: 16 }}>
            <p style={{
              fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)',
              letterSpacing: '0.08em', marginBottom: 8,
            }}>
              {t('products.composition').toUpperCase()}
            </p>
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 4,
              padding: '10px 12px',
              background: 'var(--bg-subtle)',
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--border-subtle)',
            }}>
              {product.components.map((c, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 13, color: 'var(--fg)',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  <span>• {c.component?.name || ''}</span>
                  <span style={{ color: 'var(--fg-muted)' }}>
                    {Number(c.qty)} {unitLabel(c.component?.unit)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Селекторы атрибутов */}
        {hasVariants && attributes.length > 0 && (
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {attributes.map(attrName => {
              const opts = attributeOptions[attrName] || [];
              if (opts.length === 0) return null;
              return (
                <div key={attrName}>
                  <p style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)',
                    letterSpacing: '0.08em', marginBottom: 8,
                  }}>
                    {attrName.toUpperCase()}: <span style={{ color: 'var(--fg-strong)' }}>{selected[attrName] || '—'}</span>
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {opts.map(val => {
                      const isSelected = selected[attrName] === val;
                      const available = isValueAvailable(attrName, val);
                      return (
                        <button
                          key={val}
                          onClick={() => toggleAttr(attrName, val)}
                          disabled={!available && !isSelected}
                          style={{
                            padding: '8px 14px',
                            borderRadius: 'var(--r-sm)',
                            border: '1px solid',
                            borderColor: isSelected ? 'var(--brand-primary)' : 'var(--border)',
                            background: isSelected ? 'var(--brand-primary-soft)' : 'var(--bg-elevated)',
                            color: isSelected ? 'var(--brand-primary)' : (available ? 'var(--fg)' : 'var(--fg-faint)'),
                            fontWeight: isSelected ? 700 : 500,
                            fontSize: 14,
                            cursor: (available || isSelected) ? 'pointer' : 'not-allowed',
                            opacity: (available || isSelected) ? 1 : 0.45,
                            textDecoration: (!available && !isSelected) ? 'line-through' : 'none',
                          }}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 20, display: 'flex', alignItems: 'baseline', gap: 8 }}>
          {priceDisplay.kind === 'single' ? (
            <>
              <span style={{ fontSize: 32, fontWeight: 800, color: 'var(--fg-strong)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                {(priceDisplay.value || 0).toLocaleString()}
              </span>
              <span style={{ fontSize: 14, color: 'var(--fg-muted)' }}>
                {getCurrencySymbol(shop?.currency)} / {unitLabel(product.unit)}
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--fg-strong)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                {priceDisplay.min.toLocaleString()} – {priceDisplay.max.toLocaleString()}
              </span>
              <span style={{ fontSize: 14, color: 'var(--fg-muted)' }}>
                {getCurrencySymbol(shop?.currency)} / {unitLabel(product.unit)}
              </span>
            </>
          )}
        </div>

        {isComposite(product) ? (
          <div style={{ marginTop: 8 }}>
            <span className="badge badge-green">
              <Icon name="check" size={11} stroke={2.5} style={{ marginRight: 4 }} />
              {t("productView.inStock")}
            </span>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            {displayStock > 0 ? (
              <span className="badge badge-green">
                <Icon name="check" size={11} stroke={2.5} style={{ marginRight: 4 }} />
                {t("productView.inStock")}
              </span>
            ) : (
              <span className="badge badge-red">{t("productView.outOfStock")}</span>
            )}
          </div>
        )}

        {hasVariants && !allAttrsSelected && (
          <p style={{
            marginTop: 12, fontSize: 12, color: 'var(--fg-muted)',
            fontStyle: 'italic',
          }}>
            {t('variants.pickVariant')}
          </p>
        )}
      </div>

      {shop?.cartEnabled && (isComposite(product) || displayStock > 0) && (
        <div style={{
          position: 'fixed',
          bottom: 0, left: 0, right: 0,
          padding: 12,
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
          background: 'var(--bg-elevated)',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex', gap: 8, alignItems: 'center',
          zIndex: 50,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            background: 'var(--bg-subtle)',
            borderRadius: 'var(--r-sm)',
            padding: 4, gap: 4,
          }}>
            <button onClick={() => setQty(Math.max(1, qty - 1))} style={qtyBtn}>
              <Icon name="minus" size={16} stroke={2.5} />
            </button>
            <span style={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>{qty}</span>
            <button onClick={() => setQty(qty + 1)} style={qtyBtn}>
              <Icon name="plus" size={16} stroke={2.5} />
            </button>
          </div>
          <button
            className="btn btn-primary"
            onClick={addToCart}
            disabled={!canAddToCart}
            style={{ flex: 1, opacity: canAddToCart ? 1 : 0.55 }}
          >
            {t("productView.addToCart")}
            {hasVariants && matchedVariant && ` · ${formatMoney(qty * (matchedVariant.salePrice || 0), shop?.currency)}`}
            {!hasVariants && ` · ${formatMoney(qty * (product.salePrice || 0), shop?.currency)}`}
          </button>
        </div>
      )}
    </div>
  );
}

const qtyBtn = {
  width: 32, height: 32,
  background: 'transparent', border: 'none',
  color: 'var(--fg)', cursor: 'pointer',
  borderRadius: 'var(--r-xs)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
